import ScheduleIcon from '@mui/icons-material/AccessTime';
import { Alert, Button } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';

import { TAG_CLASSES, TAG_NEUTRAL } from '@/components/feedback/tagClasses';
import { stripePromise } from '@/features/payments';
import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/currency';

import { deadlineOf } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';
import { PaymentBreakdown } from './PaymentBreakdown';
import { PaymentMethodPicker } from './PaymentMethodPicker';
import { usePayItem } from './usePayItem';

interface PayStepProps {
  item: OutstandingItem;
  onDone: () => void;
  onSkip: () => void;
}

/**
 * The paying step of the guided flow.
 *
 * Each item is confirmed and charged on its own. Batching the queue into one
 * combined charge would read as a single basket, but the endpoints settle one
 * document each — so a card declined half way would leave some items paid and
 * some not, under a total that claimed otherwise. Charging per item keeps every
 * receipt matched to the document it settles, and a failure strands only the
 * step it happened on.
 *
 * `usePayItem` picks the endpoint from the row's shape, so the step cannot be
 * pointed at the wrong one.
 */
export const PayStep = ({ item, onDone, onSkip }: PayStepProps) => (
  // Keyed on the item so moving to the next step mounts fresh Stripe iframes —
  // card details must never carry across from the previous document.
  <Elements stripe={stripePromise} key={item.id}>
    <PayStepForm item={item} onDone={onDone} onSkip={onSkip} />
  </Elements>
);

const PayStepForm = ({ item, onDone, onSkip }: PayStepProps) => {
  const payment = usePayItem(item, onDone);
  const deadline = deadlineOf(item);

  return (
    <div className="flex flex-col gap-4">
      {payment.error !== '' && (
        <Alert severity="error" role="alert" className="rounded-lg">
          {payment.error}
        </Alert>
      )}

      {/* The deadline as a tag rather than a sentence in the banner below. How
          late a payment is changes per document and is the thing the reader is
          checking; the bank-transfer advice is the same on every one. Rolling
          both into one warning made the constant part look urgent and buried
          the part that was not — and hid the deadline entirely when on time.

          Built from the tag tokens rather than <StatusTag> because this is a
          date, not a status: StatusTag picks its icon from the tone, so a late
          deadline would wear the ✕ that means "cancelled". A clock is the mark
          in both states; only the colour changes. */}
      {deadline !== undefined && (
        <div>
          {/* Announced, because "11 days late" is the one fact on this step a
              reader must not miss. The words carry it — the colour is a second
              channel, never the only one (§11). */}
          <span
            role="status"
            className={cn(
              TAG_CLASSES,
              deadline.late
                ? 'border-tag-danger-edge bg-tag-danger text-tag-danger-ink'
                : TAG_NEUTRAL,
            )}
          >
            <ScheduleIcon aria-hidden fontSize="inherit" className="text-[14px]" />
            {deadline.text}
          </span>
        </div>
      )}

      {/* Always shown, not only when overdue: a transfer takes days to clear,
          which is worth knowing BEFORE the due date passes. Shown only on late
          payments, it arrived exactly when it was too late to act on.

          No card recommendation. The ATO forbids settling a super-fund
          liability by credit card, so on an SMSF row a recommendation would
          contradict the compliance notice in the method picker below — and the
          timing fact is the same either way. One sentence that is true for
          every reader beats two that have to be kept in step with the
          `isCardDisabled` rule. */}
      <Alert severity="warning" className="rounded-lg">
        Bank transfers take longer over weekends and public holidays. Allow for that when timing
        this payment to avoid a late fee.
      </Alert>

      <PaymentBreakdown
        lines={payment.lines}
        processingFee={payment.processingFee}
        total={payment.total}
        invoiceLinks={payment.invoiceLinks}
      />

      <PaymentMethodPicker
        method={payment.method}
        onMethodChange={payment.selectMethod}
        isCardDisabled={payment.isCardDisabled}
        cards={payment.cards}
        isLoadingCards={payment.isLoadingCards}
        selectedCard={payment.selectedCard}
        onCardChange={payment.selectCard}
        cardholderName={payment.cardholderName}
        onCardholderNameChange={payment.setCardholderName}
        saveCard={payment.saveCard}
        onSaveCardChange={payment.setSaveCard}
        bankTransfer={payment.bankTransfer}
        onBankTransferChange={payment.setBankTransfer}
        fees={payment.fees}
        completion={payment.completion}
        isSubmitting={payment.isSubmitting}
      />

      <div className="flex flex-col gap-2">
        <Button
          variant="contained"
          // The same green as the row's Pay button, at full weight rather than
          // its soft fill: this is the click that actually moves the money, and
          // it has to outrank "Skip for now" sitting directly beneath it.
          color="success"
          size="large"
          onClick={() => void payment.pay()}
          disabled={!payment.canPay}
          className="w-full rounded-full"
        >
          {payment.isSubmitting ? 'Paying…' : `Pay ${formatCurrency(payment.total)}`}
        </Button>

        <Button
          onClick={onSkip}
          disabled={payment.isSubmitting}
          className="w-full rounded-full"
          color="inherit"
        >
          Skip for now
        </Button>
      </div>
    </div>
  );
};
